/**
 * Melee swing-arc feel — single knob file (config-driven, 2026-06-12).
 *
 * The swing-arc overlay (MeleeBehavior → vfx:melee_swing → VFXSystem) used
 * inline defaults of -45°..+45°: symmetric about horizontal, so HALF of every
 * swing dipped below the attacker's feet — on the ground that read as a
 * slash going through the floor (DJ's "projectile east through the floor",
 * with the un-mirrored sword_swing one-shot supplying the "east").
 *
 * Angles are DEGREES, authored for a RIGHT-facing attacker; the renderer
 * mirrors across the vertical axis when facing left. 0° = straight ahead,
 * negative = up. Lane AZ (2026-10-09) anchored the swing on the body and
 * sized it to the cone's reach with a -75°..+5° overhead-to-forward sweep.
 * Lane BB (2026-10-09, DJ: "the warriors is too big, but also the main angle
 * of the cone should be more horizontal"): the default sweep is now
 * -25°..+15° — a mostly horizontal slash centred just above facing — and the
 * smear is DRAWN at `visualReachScale` of the real reach (never below
 * `visualReachMinPx`, never past the reach). Visual only: the gameplay cone
 * (MeleeConeProfile — reach + arcDeg, symmetric about horizontal) is
 * unchanged, and impact sparks still play at the target, so a hit beyond the
 * drawn arc does not read as a miss.
 *
 * Plan 29 D55 (lane BS, 2026-10-11): the angles now come from the shared
 * `MELEE_SWING_GEOMETRY` — the same numbers the hit cone is built from — and
 * the renderer draws a faint wash over the whole real hit area
 * (`resolveMeleeCone`: full reach, the swing plus its forgiveness margin)
 * under the bright crescent. Impacts play only on a confirmed hit.
 */
import { MELEE_SWING_GEOMETRY } from '../../shared/config/melee-swing-geometry.config';
import type { MeleeSwingStyle } from '../../shared/config/meleeSwingStyles';

export const MELEE_SWING_CONFIG = {
  /** Arc start angle in degrees (lane BB: a little above facing). */
  arcAngle: -MELEE_SWING_GEOMETRY.swingUpDeg,
  /** Total sweep in degrees (start + spread = finish, a little below facing). */
  arcSpread: MELEE_SWING_GEOMETRY.swingUpDeg + MELEE_SWING_GEOMETRY.swingDownDeg,
  /** Swing draw lifetime (ms). */
  durationMs: 150,
  /** Arc stroke width (px). */
  lineWidth: 4,
  /** Default stroke color (per-ability lineColor overrides win). */
  color: 0xFF4444,
  /** Arc stroke alpha at swing start (fades to 0 over durationMs). */
  alpha: 0.8,
  /** How much the arc grows over its lifetime (0.5 = 1.0x → 1.5x). */
  scaleUp: 0.5,
  /** Arc render depth (combat overlay layer). */
  depth: 95,

  // ── Lane AZ (plan 23 row 50): body-anchored smear sized to the reach ──
  /** Mid-swing smear thickness as a fraction of the reach (tapers to 0 at both ends). */
  smearThickness: 0.42,
  /** Smear fill alpha at swing start. */
  smearAlpha: 0.4,
  /** Faint swept-area fill from the body edge to the reach (joins the smear to the body). */
  wedgeAlpha: 0.14,
  /** Where the swept area starts (px from body centre) — the 40 px body's edge. */
  wedgeInnerPx: 22,
  /** The swing rotates INTO place: it starts this many degrees back and eases
   *  forward (lane BB: 40 → 25 so the early frames stay near horizontal too). */
  sweepLeadDeg: 25,
  /** Smear starts at this scale and eases to 1.0 — never past the reach. */
  startScale: 0.9,
  /** Lane BB: the smear is drawn at this fraction of the real reach (visual only). */
  visualReachScale: 0.65,
  /** …but never smaller than this (px), so short-reach swings stay readable. */
  visualReachMinPx: 80,
  /** Bright leading-edge stroke drawn on the reach arc. */
  edgeWidth: 3,
  /** How far the edge colour is mixed toward white (0..1). */
  edgeLighten: 0.55,
  /** Used when an ability authors no colour and has no school colour. */
  fallbackColor: 0xE8E8F0,
  /** Plan 29 D55: alpha of the faint wash over the whole real hit area. */
  realAreaAlpha: 0.09,
} as const;

/**
 * Per-ability swing looks. An ability not listed gets the smear. `lightning`
 * = a crackling bolt from the hand to the target plus a contact spark burst
 * (Lightning Touch, DJ's named example). Plan 29 F9 (lane BX): every melee
 * swing in the warrior and paladin kits has its own style, so no two swings in
 * one kit read alike (`kitIdentity.test.ts`).
 */
export type { MeleeSwingStyle } from '../../shared/config/meleeSwingStyles';

export const MELEE_SWING_STYLES: Readonly<Record<string, MeleeSwingStyle>> = {
  shaman_auto: 'lightning',
  // Warrior: the auto keeps the reference smear.
  mortal_strike: 'rend',
  execute: 'guillotine',
  cleave: 'cleave',
  charge: 'ram',
  // Paladin.
  paladin_auto: 'hammer',
  crusader_strike: 'crusade',
  shield_of_the_righteous: 'shield_bash',
  // Rogue (lane BY): the auto throws stars; the three strikes are a dagger
  // stab, a finishing flurry and a killing lunge.
  backstab: 'stab',
  eviscerate: 'flurry',
  assassinate: 'lunge',
  // Shaman (lane CA): the auto keeps Lightning Touch's bolt; Stormstrike is
  // a storm-blue crescent with a bolt dropping onto the reach.
  stormstrike: 'storm',
};

/** The accent drawn over (or instead of) a styled swing's crescent. */
export type SwingAccent = 'gash' | 'drop_blade' | 'glints' | 'chevrons' | 'hammer_head' | 'cross' | 'shield'
  | 'dagger' | 'cross_cuts' | 'thrust' | 'thunderbolt';

/**
 * Shape knobs per authored style (lane BX). Everything is drawn once at spawn
 * inside the drawn swing radius (`smearDrawRadius`) and animated by
 * transform/alpha only; reduced motion drops rotation, drops and slides and
 * keeps each shape at its final place.
 */
export interface SwingStyleLook {
  /** Draw the tapered crescent under the accent. */
  crescent: boolean;
  /** Crescent mid-swing thickness, fraction of the drawn radius. */
  thickness: number;
  smearAlpha: number;
  /** Bright reach-edge stroke width (px) and its mix toward white. */
  edgeWidth: number;
  edgeLighten: number;
  /** A second crescent at this fraction of the radius (0 = none). */
  innerEcho: number;
  /** Degrees the crescent rotates into place. */
  sweepLeadDeg: number;
  /** Lifetime (ms); the swing event's duration is the floor. */
  durationMs: number;
  accent: SwingAccent;
  /** Accent colour mix toward white (0..1). */
  accentLighten: number;
}

type StyledSwing = Exclude<MeleeSwingStyle, 'smear' | 'lightning'>;

export const SWING_STYLE_LOOKS: Readonly<Record<StyledSwing, SwingStyleLook>> = {
  // Mortal Strike: heavier than the auto, and a wound left at the reach.
  rend: { crescent: true, thickness: 0.56, smearAlpha: 0.55, edgeWidth: 4, edgeLighten: 0.35, innerEcho: 0, sweepLeadDeg: 25, durationMs: 300, accent: 'gash', accentLighten: 0.15 },
  // Execute: a thin fast edge, then the blade falls.
  guillotine: { crescent: true, thickness: 0.16, smearAlpha: 0.5, edgeWidth: 3, edgeLighten: 0.8, innerEcho: 0, sweepLeadDeg: 20, durationMs: 320, accent: 'drop_blade', accentLighten: 0.7 },
  // Cleave: wide and doubled — it hits everything in front.
  cleave: { crescent: true, thickness: 0.34, smearAlpha: 0.42, edgeWidth: 3, edgeLighten: 0.6, innerEcho: 0.62, sweepLeadDeg: 25, durationMs: 240, accent: 'glints', accentLighten: 0.85 },
  // Charge: a shoulder, not a blade.
  ram: { crescent: false, thickness: 0, smearAlpha: 0, edgeWidth: 5, edgeLighten: 0.45, innerEcho: 0, sweepLeadDeg: 0, durationMs: 260, accent: 'chevrons', accentLighten: 0.3 },
  // Paladin auto: the hammer itself travels the arc.
  hammer: { crescent: true, thickness: 0.2, smearAlpha: 0.32, edgeWidth: 2, edgeLighten: 0.5, innerEcho: 0, sweepLeadDeg: 0, durationMs: 230, accent: 'hammer_head', accentLighten: 0.25 },
  // Crusader Strike: a pale blade and the cross it calls down.
  crusade: { crescent: true, thickness: 0.3, smearAlpha: 0.45, edgeWidth: 3, edgeLighten: 0.75, innerEcho: 0, sweepLeadDeg: 25, durationMs: 340, accent: 'cross', accentLighten: 0.6 },
  // Shield of the Righteous: the shield is the weapon.
  shield_bash: { crescent: false, thickness: 0, smearAlpha: 0, edgeWidth: 5, edgeLighten: 0.5, innerEcho: 0, sweepLeadDeg: 0, durationMs: 300, accent: 'shield', accentLighten: 0.35 },
  // Backstab: no arc — a short blade darts in and a puncture flares (lane BY).
  stab: { crescent: false, thickness: 0, smearAlpha: 0, edgeWidth: 3, edgeLighten: 0.6, innerEcho: 0, sweepLeadDeg: 0, durationMs: 240, accent: 'dagger', accentLighten: 0.6 },
  // Eviscerate: a red crescent, then two cuts cross over the target.
  flurry: { crescent: true, thickness: 0.3, smearAlpha: 0.4, edgeWidth: 3, edgeLighten: 0.45, innerEcho: 0, sweepLeadDeg: 30, durationMs: 340, accent: 'cross_cuts', accentLighten: 0.4 },
  // Assassinate: a hairline edge and one long killing thrust.
  lunge: { crescent: true, thickness: 0.1, smearAlpha: 0.3, edgeWidth: 2, edgeLighten: 0.7, innerEcho: 0, sweepLeadDeg: 15, durationMs: 380, accent: 'thrust', accentLighten: 0.55 },
  // Stormstrike: a storm-blue crescent, then a bolt drops onto the reach (lane CA).
  storm: { crescent: true, thickness: 0.38, smearAlpha: 0.45, edgeWidth: 3, edgeLighten: 0.65, innerEcho: 0, sweepLeadDeg: 25, durationMs: 340, accent: 'thunderbolt', accentLighten: 0.7 },
};

export const LIGHTNING_TOUCH_CONFIG = {
  /** Total lifetime of bolt + sparks (ms). */
  durationMs: 240,
  /** Bolt segments between hand and contact. */
  segments: 7,
  /** Max sideways displacement of a bolt kink (px). */
  jitterPx: 14,
  /** Soft halo stroke under each bolt: width (px) and alpha. */
  haloWidth: 12,
  haloAlpha: 0.22,
  /** Crisp bolt: coloured stroke + near-white core (px / alpha). */
  glowWidth: 5,
  glowAlpha: 0.85,
  coreWidth: 2,
  coreAlpha: 1,
  /** Two bolts alternate (alpha only) this many times over the lifetime = crackle. */
  crackleFlips: 4,
  /** Contact spark burst: ray count, inner/outer radius (px), stroke width. */
  sparkCount: 8,
  sparkInner: 5,
  sparkOuter: 22,
  sparkWidth: 2,
  /** Spark burst grows from this scale to 1.0 as it fades. */
  sparkStartScale: 0.55,
  /** Bolt colour when the ability authors none (shaman teal). */
  color: 0x00B3A4,
} as const;
